/**
 * Firm-tier step-up (ADR-0047 R2, v9.8 T6-S1).
 *
 * The firm tier answers 401 with `code: 'mfa_required'` (the member's stamp is
 * missing or older than 12 h) or `code: 'step_up_required'` (POST
 * /firm/session/enter saw a sign-in older than 10 minutes). Both carry a
 * `step_up_url` — `/api/auth/authorize?prompt=login&return_to=…` — that runs a
 * real WorkOS sign-in and lands back on the engagement with `?step_up=1`; the
 * page then calls `/session/enter` once. Unsaved form state is parked in
 * sessionStorage across that round trip (T6-B4) and restored on return.
 */
import { ApiError } from './api';

export const STEP_UP_PARAM = 'step_up';
const DRAFT_KEY = (eng: string) => `gp-firm-draft:${eng}`;

/** The step-up URL when `err` is the firm tier asking for a fresh sign-in; else null. */
export function stepUpUrlOf(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 401) return null;
  const b = (err.body ?? {}) as { code?: unknown; error?: unknown; step_up_url?: unknown };
  const code = typeof b.code === 'string' ? b.code : typeof b.error === 'string' ? b.error : '';
  if (code !== 'mfa_required' && code !== 'step_up_required') return null;
  const url = typeof b.step_up_url === 'string' ? b.step_up_url : '';
  // Same-origin path only (the server builds it; never follow anything else).
  return url.startsWith('/api/auth/authorize?') ? url : null;
}

/** Leave for the WorkOS sign-in. Callers park unsaved input first (`saveDraft`). */
export function startStepUp(url: string): void {
  window.location.assign(url);
}

/** Park unsaved form fields for this engagement across the sign-in round trip. */
export function saveDraft(eng: string, draft: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(DRAFT_KEY(eng), JSON.stringify({ ...readDraft(eng), ...draft }));
  } catch {
    /* storage unavailable: the step-up still works, the draft is lost */
  }
}

export function readDraft(eng: string): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY(eng));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Take (read + forget) one parked field. */
export function takeDraft<T = unknown>(eng: string, key: string): T | undefined {
  const all = readDraft(eng);
  if (!(key in all)) return undefined;
  const value = all[key] as T;
  delete all[key];
  try {
    if (Object.keys(all).length) sessionStorage.setItem(DRAFT_KEY(eng), JSON.stringify(all));
    else sessionStorage.removeItem(DRAFT_KEY(eng));
  } catch {
    /* ignore */
  }
  return value;
}
